if (!customElements.get('media-gallery')) {
  const debounce = window.debounce || function (fn, ms = 100) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  };

  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.sectionId = this.dataset.sectionId;
        this.id = this.sectionId;

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');

        if (!this.elements.thumbnails) {
          if (this.elements.viewer) {
            this.elements.viewer.addEventListener(
              'slideChanged',
              debounce(this.onSlideChanged.bind(this), 300)
            );
          }
          return;
        }

        this.elements.viewer.addEventListener(
          'slideChanged',
          debounce(this.onSlideChanged.bind(this), 300)
        );

        /* Delegated so thumbnails swapped in after a shape or metal change
           still work (the gallery only renders the selected variant's images). */
        this.elements.thumbnails.addEventListener('click', (event) => {
          const btn = event.target.closest('button');
          if (!btn) return;
          const mediaToSwitch = btn.closest('[data-target]');
          if (!mediaToSwitch || !this.elements.thumbnails.contains(mediaToSwitch)) return;
          this._userThumbClick = true;
          this.setActiveMedia(mediaToSwitch.dataset.target, false);
        });

        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches)
          this.removeListSemantic();


        const getViewerId = () => `${this.id}-viewer`;

        const force3DMain = () => {
          const viewerId = getViewerId();
          const viewerSlide = this.elements.viewer.querySelector(`[data-media-id="${viewerId}"]`);
          if (!viewerSlide) return;
          viewerSlide.classList.add('is-active');
          this.elements.viewer.querySelectorAll('.slider__slide').forEach((el) => {
            if (el !== viewerSlide) el.classList.remove('is-active');
          });
          try {
            viewerSlide.parentElement.scrollTo({
              left: viewerSlide.offsetLeft,
              behavior: 'instant'
            });
          } catch (e) {
            viewerSlide.parentElement.scrollTo(viewerSlide.offsetLeft, 0);
          }

          force3DThumb();
        };

        const force3DThumb = () => {
          const viewerId = getViewerId();
          const viewerThumb = this.elements.thumbnails.querySelector(`[data-target="${viewerId}"]`);
          if (!viewerThumb) return;

          this.elements.thumbnails.querySelectorAll('button').forEach(btn => btn.removeAttribute('aria-current'));
          const btn = viewerThumb.querySelector('button');
          if (btn) btn.setAttribute('aria-current', 'true');

          try {
            this.elements.thumbnails.slider?.scrollTo({
              left: viewerThumb.offsetLeft,
              behavior: 'instant'
            });
          } catch (e) {
            viewerThumb.parentElement.scrollTo(viewerThumb.offsetLeft, 0);
          }
        };

        this._thumbObserver = new MutationObserver((mutations) => {
          const viewerId = getViewerId();
          const viewerThumb = this.elements.thumbnails.querySelector(`[data-target="${viewerId}"]`);
          if (!viewerThumb) return;

          let needRestore = false;

          for (const m of mutations) {
            if (m.type === 'attributes' && m.attributeName === 'aria-current') {
              const current = this.elements.thumbnails.querySelector('button[aria-current="true"]');
              if (current && !viewerThumb.contains(current)) {
                needRestore = true;
                break;
              }
            } else if (m.type === 'childList') {
              needRestore = true;
              break;
            }
          }

          if (needRestore) {
            setTimeout(() => {
              force3DThumb();
            }, 20);
          }
        });

        this._thumbObserver.observe(this.elements.thumbnails, {
          attributes: true,
          subtree: true,
          childList: true,
          attributeFilter: ['aria-current']
        });

        const reinforceForce3DThumbSeries = (tries = 6, interval = 60) => {
          let i = 0;
          const id = setInterval(() => {
            force3DThumb();
            i++;
            if (i >= tries) clearInterval(id);
          }, interval);
        };

        const reinforceForce3DMainSeries = (tries = 8, interval = 80) => {
          let i = 0;
          const id = setInterval(() => {
            force3DMain();
            i++;
            if (i >= tries) clearInterval(id);
          }, interval);
        };

        /* Selecting a metal / center stone / hidden stone is a variant change,
           and Dawn points the gallery at that variant's still via setActiveMedia().
           On this builder the 3D viewer is always the hero, so any programmatic
           (non-viewer) switch snaps back. A genuine thumbnail tap sets
           _userThumbClick, so the user can still open the stills by hand. */
        this._origSetActiveMedia = this.setActiveMedia.bind(this);
        this.setActiveMedia = (mediaId, prepend) => {
          this._origSetActiveMedia(mediaId, prepend);
          const isViewer = mediaId && String(mediaId).endsWith('viewer');
          if (!isViewer && !this._userThumbClick) {
            force3DMain();
            reinforceForce3DMainSeries(4, 80);
          }
          this._userThumbClick = false;
        };

        requestAnimationFrame(() => {
          setTimeout(() => {
            force3DMain();
            reinforceForce3DMainSeries(8, 80);
          }, 60);
        });

        /* The 3D viewer is an iframe that sizes late on mobile, so the swipe
           slider can settle on the first still image before the viewer is
           ready. Re-assert the viewer as the active slide once the page and the
           iframe have finished loading, so mobile can no longer drift off it. */
        window.addEventListener('load', () => {
          force3DMain();
          reinforceForce3DMainSeries(6, 120);
        });

        const mesh3dFrame = document.getElementById('mesh3d-viewer');
        if (mesh3dFrame) {
          mesh3dFrame.addEventListener('load', () => {
            setTimeout(() => {
              force3DMain();
              reinforceForce3DMainSeries(5, 120);
            }, 40);
          });
        }


        document.addEventListener('variant:changed', () => {
          setTimeout(() => {
            force3DMain();
            reinforceForce3DThumbSeries(8, 60);
          }, 80);
        });

        if (this.elements.viewer.resetPages) {
          const originalReset = this.elements.viewer.resetPages.bind(this.elements.viewer);
          this.elements.viewer.resetPages = () => {
            try { originalReset(); } catch (e) { /* ignore */ }
            setTimeout(() => {
              force3DMain();
              reinforceForce3DThumbSeries(5, 60);
            }, 60);
          };
        }

        if (this.elements.thumbnails && this.elements.thumbnails.resetPages) {
          const origThumbReset = this.elements.thumbnails.resetPages.bind(this.elements.thumbnails);
          this.elements.thumbnails.resetPages = () => {
            try { origThumbReset(); } catch (e) { /* ignore */ }
            setTimeout(() => {
              force3DThumb();
              reinforceForce3DThumbSeries(4, 60);
            }, 60);
          };
        }

        this.elements.viewer.addEventListener('slideChanged', debounce(() => {
          setTimeout(() => {
            force3DThumb();
          }, 30);
        }, 50));

      } 

      onSlideChanged(event) {
        const current = event.detail && event.detail.currentElement;
        if (!current) return;

        if (current.dataset.mediaId && current.dataset.mediaId.endsWith('viewer')) {
          const viewerThumb = this.elements.thumbnails.querySelector(
            `[data-target="${this.id}-viewer"]`
          );
          if (viewerThumb) this.setActiveThumbnail(viewerThumb);
          return;
        }

        if (current.dataset.mediaId) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${current.dataset.mediaId}"]`
          );
          if (thumbnail) this.setActiveThumbnail(thumbnail);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );

        if (!activeMedia) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((el) => {
          el.classList.remove('is-active');
        });

        activeMedia.classList.add('is-active');

        if (prepend) {
          try {
            if (activeMedia.parentElement.firstChild !== activeMedia)
              activeMedia.parentElement.prepend(activeMedia);
          } catch (e) { /* ignore */ }

          if (this.elements.thumbnails) {
            const activeThumb = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumb && activeThumb.parentElement.firstChild !== activeThumb)
              activeThumb.parentElement.prepend(activeThumb);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        setTimeout(() => {
          try {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          } catch (e) { /* ignore */ }
        });

        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;

        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );

        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail);

          const pos = activeThumbnail.dataset.mediaPosition || 1;
          this.announceLiveRegion(activeMedia, pos);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails.querySelectorAll('button')
          .forEach((btn) => btn.removeAttribute('aria-current'));

        const btn = thumbnail.querySelector('button');
        if (btn) btn.setAttribute('aria-current', true);

        if (this.elements.thumbnails.isSlideVisible && !this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
          try {
            this.elements.thumbnails.slider.scrollTo({
              left: thumbnail.offsetLeft,
            });
          } catch (e) {
            thumbnail.parentElement.scrollTo(thumbnail.offsetLeft, 0);
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (window.pauseAllMedia) window.pauseAllMedia();
        const deferred = activeItem.querySelector('.deferred-media');
        if (deferred && typeof deferred.loadContent === 'function') deferred.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (this.stickyHeader)
          this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}